// @flow

import * as React from 'react';
import styled from 'styled-components';
import { colors, fontWeight, padding } from '../../constants';
import MinimizeIcon from '../../icons/minimize';
import MaximizeIcon from '../../icons/maximize';
import XIcon from '../../icons/xicon';
import LoadingSpinner from '../loading_spinner';
import CheckMarkCircle from '../../icons/checkmark_circle';
import ErrorIcon from '../../icons/error';

type Status = 'pending' | 'success' | 'error' | 'normal';

export type Props = {
  title: string,
  width: number,
  maxHeight: number,
  children: React.Node,
  status: Status,
  showCloseIcon: boolean,
  onClose: (Event) => void,
  expanded?: boolean,
  onExpandChange?: (void) => void,
  customHeaderColor?: string,
};

export default function Tray({
  width,
  maxHeight,
  title,
  children,
  status,
  showCloseIcon,
  onClose,
  customHeaderColor,
  expanded,
  onExpandChange,
}: Props) {
  let icon;

  if (status === 'pending') {
    icon = <LoadingSpinner size="small" flavor="doubleGreen" />;
  } else if (status === 'success') {
    icon = <CheckMarkCircle color={colors.white} />;
  } else if (status === 'error') {
    icon = <ErrorIcon color={colors.white} />;
  }

  return (
    <Container width={width} expanded={expanded}>
      <Header
        customHeaderColor={customHeaderColor}
        status={status}
        onClick={onExpandChange}
      >
        {icon && <IconContainer>{icon}</IconContainer>}

        <Label>{title}</Label>

        <Controls>
          <Control onClick={onExpandChange} data-provides="tray-expand">
            {expanded && <MinimizeIcon />}

            {!expanded && <MaximizeIcon />}
          </Control>

          {showCloseIcon && (
            <Control onClick={onClose} data-provides="tray-close">
              <XIcon color={colors.white} hoverColor={colors.white} />
            </Control>
          )}
        </Controls>
      </Header>

      {expanded && (
        <Content maxHeight={maxHeight} key="content" className="tray-content">
          {children}
        </Content>
      )}
    </Container>
  );
}

Tray.defaultProps = {
  width: 180,
  maxHeight: 250,
  expandedByDefault: false,
  status: 'normal',
  notify: true,
  showCloseIcon: false,
  onClose: () => {},
};

const colorForStatus = (status: Status) => {
  if (status === 'success') {
    return colors.mediumGreenhouseGreen;
  } else if (status === 'error') {
    return colors.darkRed;
  } else {
    return colors.evergreen;
  }
};

const Container = styled.div`
  width: ${(props) => props.width}px;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.1);
`;

const Control = styled.div`
  cursor: pointer;
  display: inline-block;

  & + & {
    margin-left: ${padding.tiny};
  }
`;

const Controls = styled.div`
  margin-left: auto;
  white-space: nowrap;
`;

const Content = styled.div`
  background-color: ${colors.white};
  border-left: 1px solid ${colors.lightGrey};
  border-right: 1px solid ${colors.lightGrey};
  max-height: ${(props) => props.maxHeight}px;
  overflow-x: hidden;
  overflow-y: scroll;
`;

const Header = styled.div`
  color: ${colors.white};
  font-weight: ${fontWeight.heavy};
  font-size: 14px;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 10px 14px;
  box-sizing: border-box;
  background-color: ${(props) =>
    props.customHeaderColor || colorForStatus(props.status)};
  cursor: pointer;
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;

  svg {
    display: block;
  }
`;

const IconContainer = styled.div`
  margin-right: 10px;
  padding-top: 2px;
`;

const Label = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
